import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Heart, 
  Info, 
  CheckCircle,
  Euro,
  PawPrint,
  Bed
} from 'lucide-react';

const PracticalInfoSection = () => {
  const practicalInfo = [
    {
      icon: PawPrint,
      title: "Animaux admis",
      description: "Vos compagnons à quatre pattes sont les bienvenus dans notre résidence",
      highlight: "Gratuit"
    },
    {
      icon: Bed,
      title: "Forfait préparation & linge",
      description: "Préparation de l'appartement et linge de maison complet : lits faits à votre arrivée, grandes et petites serviettes, gants de toilettes et changement de draps en cours de séjour",
      highlight: "50€/séjour"
    }
  ];

  const additionalServices = [
    "Lits faits à votre arrivée",
    "Grandes et petites serviettes fournies",
    "Gants de toilettes inclus",
    "Changement de draps en cours de séjour",
    "Ménage de fin de séjour inclus dans le forfait",
    "Préparation et vérification de l'appartement",
    "Accueil personnalisé à votre arrivée",
    "Animaux acceptés sans supplément",
    "Parking privé gratuit pour tous"
  ];

  return (
    <section id="infos-pratiques" className="py-24 bg-gradient-to-b from-white to-emerald-50/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="px-5 py-3 text-sm font-semibold bg-emerald-100 text-emerald-800 border-2 border-emerald-200 shadow-lg mb-4">
              <Info className="h-4 w-4 mr-2" />
              Informations pratiques
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Tout ce qu'il faut 
              <span className="bg-gradient-to-r from-emerald-700 to-emerald-600 bg-clip-text text-transparent drop-shadow-sm"> savoir</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez nos services inclus et nos conditions d'accueil pour un séjour en toute sérénité aux Platanes.
            </p>
          </div>

          {/* Informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {practicalInfo.map((info, index) => (
              <Card key={index} className="border-2 border-emerald-200 shadow-xl hover:shadow-2xl transition-all duration-500 hover:border-emerald-300 group card-hover">
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-emerald-100 to-emerald-100 p-4 rounded-2xl inline-block group-hover:scale-110 transition-transform duration-500">
                      <info.icon className="h-12 w-12 text-emerald-700" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {info.title}
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed mb-4">
                    {info.description}
                  </p>
                  <div className="inline-flex items-center space-x-2">
                    <Euro className="h-5 w-5 text-emerald-600" />
                    <Badge className="bg-emerald-600 text-white text-lg px-4 py-2 font-semibold">
                      {info.highlight}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Services inclus */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-50 rounded-2xl p-8 border-2 border-emerald-200 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <Heart className="h-8 w-8 text-emerald-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Services inclus & conditions</h3>
                </div>
                <p className="text-gray-700 mb-6 text-lg">
                  Nous mettons tout en œuvre pour que votre séjour soit parfait, avec des services de qualité et un accueil chaleureux.
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {additionalServices.map((service, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-gray-700">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-emerald-100">
                <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <Info className="h-6 w-6 text-emerald-600 mr-3" />
                  Politique intérieure
                </h4>
                <div className="space-y-4">
                  <div className="bg-emerald-100 p-4 rounded-lg border border-emerald-200">
                    <p className="text-emerald-800 font-semibold mb-2">
                      🐕 Animaux bienvenus !
                    </p>
                    <p className="text-emerald-700 text-sm">
                      Vos compagnons sont accueillis gratuitement dans tous nos appartements.
                    </p>
                  </div>
                  
                  <div className="bg-emerald-100 p-4 rounded-lg border border-emerald-200">
                    <p className="text-emerald-800 font-semibold mb-2">
                      🛏️ Forfait tout inclus - 50€/séjour
                    </p>
                    <p className="text-emerald-700 text-sm">
                      Lits faits à l'arrivée • Serviettes (grandes/petites) • Gants de toilettes • Changement draps en cours de séjour
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PracticalInfoSection;